import React, { MouseEvent, useEffect, useState } from 'react';
import { emptyStateInboxGraphic } from '@workday/canvas-graphics-web';
import { Graphic, PrimaryButton } from '@workday/canvas-kit-react';
import styles from './SessionTimeout.module.scss';
import classNames from 'classnames';
import { autoId } from '../../utils/AutomationUtil';
import { ID_RESET_IDLE_SESSION } from '../../autoIds';

export type SessionTimeoutProps = {
    getRemainingTime: () => number;
    onResetClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    textProps: SessionTimeoutTextProps;
};

interface SessionTimeoutTextProps {
    header: string;
    description: string;
    resetButtonText?: string;
}

interface FormattedTime {
    minutes: string;
    seconds: string;
}

export function formatTimeRemaining(timeRemainingMillis: number): FormattedTime {
    const minutes = Math.floor((timeRemainingMillis / (1000 * 60)) % 60);
    const seconds = Math.floor((timeRemainingMillis / 1000) % 60);

    return {
        minutes: minutes.toString(),
        seconds: seconds < 10 ? '0' + seconds.toString() : seconds.toString()
    };
}

export function SessionTimeout({ getRemainingTime, onResetClick, className, textProps }: SessionTimeoutProps) {
    const [timeRemaining, setTimeRemaining] = useState(() => {
        return getRemainingTime();
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRemaining(getRemainingTime());
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    });

    const { minutes, seconds } = formatTimeRemaining(timeRemaining);

    return (
        <div className={classNames(styles.sessionTimeout, className)}>
            <Graphic src={emptyStateInboxGraphic} width={180} height={90} />
            <h1 className={styles.heading}>{textProps.header}</h1>
            <p className={styles.message}>{textProps.description}</p>
            <p className={styles.timeRemaining}>
                {minutes}:{seconds}
            </p>
            {onResetClick && textProps.resetButtonText && (
                <PrimaryButton onClick={onResetClick} type='button' {...autoId(ID_RESET_IDLE_SESSION)}>
                    {textProps.resetButtonText}
                </PrimaryButton>
            )}
        </div>
    );
}
